import { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { PlusCircle, FolderKanban } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

export default function Projects() {
  const { activeWorkspaceId, can } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", number: "", location: "", tonnage: "", description: "" });

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/workspaces/${activeWorkspaceId}/projects`); setItems(data); }
    catch { /* 401 → auth interceptor; transport failure → connection banner */ }
  }, [activeWorkspaceId]);
  useEffect(() => {
    if (activeWorkspaceId) load();
  }, [activeWorkspaceId, load]);

  const submit = async () => {
    try {
      await api.post(`/workspaces/${activeWorkspaceId}/projects`, form);
      toast.success("Project created");
      setOpen(false);
      setForm({ name: "", number: "", location: "", tonnage: "", description: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="p-8 max-w-[1600px] mx-auto" data-testid="projects-page">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="label-cap text-amber-500">// PROJECT REGISTRY</div>
          <h1 className="font-heading text-3xl font-extrabold text-slate-100 mt-1">Projects</h1>
          <p className="text-slate-400 text-sm mt-1">Contracts, specifications, addenda, and related steel-fab documents.</p>
        </div>
        {can("create") && (
          <button onClick={() => setOpen(true)} className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold px-4 py-2 rounded-md text-sm inline-flex items-center gap-2" data-testid="new-project-button">
            <PlusCircle className="w-4 h-4" /> New project
          </button>
        )}
      </div>

      <div className="sc-card overflow-hidden">
        <table className="sc-table w-full">
          <thead>
            <tr>
              <th>Project</th>
              <th>Job #</th>
              <th>Location</th>
              <th>Tonnage</th>
              <th>Docs</th>
              <th>Findings</th>
              <th>Critical</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.id} data-testid={`project-row-${p.id}`}>
                <td>
                  <Link to={`/projects/${p.id}`} className="text-slate-100 hover:text-amber-400 font-semibold">{p.name}</Link>
                  <div className="text-xs text-slate-500">{p.description || ""}</div>
                </td>
                <td className="font-mono text-xs">{p.number || "—"}</td>
                <td>{p.location || "—"}</td>
                <td className="font-mono">{p.tonnage || "—"}</td>
                <td className="font-mono">{p.doc_count}</td>
                <td className="font-mono">{p.findings_count}</td>
                <td>
                  <span className={`px-2 py-0.5 rounded text-xs font-mono ${p.critical_count ? "sev-critical" : "bg-slate-800/80 text-slate-400"}`}>{p.critical_count}</span>
                </td>
                <td className="text-right">
                  <Link to={`/projects/${p.id}`} className="text-amber-400 hover:text-amber-300 text-xs" data-testid={`open-project-${p.id}`}>Open →</Link>
                </td>
              </tr>
            ))}
            {!items.length && (
              <tr><td colSpan={8} className="text-center py-10 text-slate-500">
                <FolderKanban className="w-8 h-8 mx-auto mb-2 text-slate-700" />
                No projects yet.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-slate-900 border-slate-700 text-slate-100">
          <DialogHeader><DialogTitle className="font-heading">New Project</DialogTitle></DialogHeader>
          {["name", "number", "location", "tonnage", "description"].map((k) => (
            <label className="block" key={k}>
              <span className="label-cap capitalize">{k}</span>
              <input value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                className="mt-1 w-full bg-slate-950/60 border border-slate-800 rounded-md px-3 py-2 text-slate-100 focus:outline-none focus:border-amber-500"
                data-testid={`new-project-${k}-input`} />
            </label>
          ))}
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="px-3 py-2 text-sm text-slate-400 hover:text-slate-200" data-testid="new-project-cancel">Cancel</button>
            <button onClick={submit} className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold px-4 py-2 rounded-md text-sm" data-testid="new-project-submit">Create</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
